<template>
	<div class="app-field input-wrapper flex flex-col" :class="wrapperClass">
		<input
			ref="input-el"
			v-model="inputValue"
			class="border rounded relative bg-[var(--background-primary)] h-full"
			:required="required"
			:disabled="isDisabled"
			v-bind="{ ...$attrs }"
			@blur="blur"
		/>

		<span v-if="showDisplayDate" class="text-sm">
			{{ getDisplayDate(modelValue) }}
		</span>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, useTemplateRef } from "vue";
import { useUserStore } from "@/store/useUserStore";
import { getDisplayDate } from "@/helpers/uiHelpers";
import {
	fuzzyDateParse,
	type FuzzyDateParseOptions,
} from "@loanlink-nl/migration";

defineOptions({
	inheritAttrs: false,
});

const props = withDefaults(
	defineProps<{
		required?: boolean;
		readOnly?: boolean;
		wrapperClass?: string;
		showDisplayDate?: boolean;
		twoDigitYearCentury?: FuzzyDateParseOptions["twoDigitYearCentury"];
	}>(),
	{
		required: false,
		readOnly: false,
		showDisplayDate: true,
		twoDigitYearCentury: "future",
	},
);

const modelValue = defineModel<string>();
const inputValue = ref<string | undefined>(modelToInput(modelValue.value));
const showError = ref(false);
const inputEl = useTemplateRef("input-el");

const userStore = useUserStore();
const isDisabled = computed(() => {
	return props.readOnly || userStore.user?.role !== "intermediary";
});

function modelToInput(value?: string): string {
	const date = fuzzyDateParse(value, {
		twoDigitYearCentury: props.twoDigitYearCentury,
	});
	return date.isValid ? date.toFormat("d-M-yyyy") : "";
}

function inputToModel(value?: string): string | undefined {
	const date = fuzzyDateParse(value, {
		twoDigitYearCentury: props.twoDigitYearCentury,
	});
	return date.isValid ? date.toISODate() : undefined;
}

function isValid(inputValue?: string) {
	if (!inputValue) {
		return true;
	}

	return fuzzyDateParse(inputValue, {
		twoDigitYearCentury: props.twoDigitYearCentury,
	}).isValid;
}

watch(inputValue, (newValue) => {
	modelValue.value = inputToModel(newValue);
});

watch(modelValue, (newValue) => {
	if (inputToModel(inputValue.value) !== newValue) {
		inputValue.value = modelToInput(newValue);
	}
});

function blur() {
	if (document.activeElement === inputEl.value) {
		return;
	}

	modelValue.value = inputToModel(inputValue.value);

	if (isValid(inputValue.value)) {
		inputValue.value = modelToInput(modelValue.value);
		return;
	}

	showError.value = true;
}
</script>

<style lang="scss">
.formkit-inner .input-wrapper input {
	height: unset;
}
</style>
